import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Lock, Check } from 'lucide-react';
import { useCourse, isStepUnlocked, canStartToday, type CourseStep } from '@/hooks/useCourse';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { Loader } from '@/components/Loader';
import { Mascot } from '@/components/Mascot';

// ════════════════════════════════════════════════════════════════
// «Путь со Славой» — экран дорожки (Фаза 2)
// Пройдено — зелёным, «ты здесь» — пульс, впереди — интригующие
// заголовки (curiosity gap). Заблокированные (не free, без подписки) — замок.
// ════════════════════════════════════════════════════════════════

const MILESTONE: Record<number, string> = { 3: '🌱', 21: '🍎' };

type NodeState = 'done' | 'current' | 'future';

export default function PathScreen() {
  const nav = useNavigate();
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const { steps, progress, loading } = useCourse();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader label="Загружаем твой Путь…" />
      </div>
    );
  }

  // Ещё не начал — приглашение
  if (!progress) {
    return (
      <div className="px-5 pt-6 pb-28 max-w-md mx-auto">
        <BackHeader onBack={() => nav(-1)} />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-elevated border-emerald-glow/40 p-6 text-center mt-6"
        >
          <Mascot pose="wave" className="w-28 h-28 mx-auto mb-3" />
          <h1 className="font-display text-2xl font-bold mb-2">Путь со Славой</h1>
          <p className="text-sm text-ink-dim leading-relaxed mb-5">
            21 день — один лёгкий шаг в день.{' '}
            {profile?.subscription_status === 'active' || profile?.subscription_status === 'trial'
              ? ''
              : 'Первые 3 дня бесплатно. '}
            Без марафона и осуждения: в своём темпе, с поддержкой. Начнём?
          </p>
          <button
            onClick={() => { haptic('medium'); nav('/path/start'); }}
            className="w-full py-3 rounded-xl bg-emerald-glow text-bg font-bold active:scale-[0.98] transition-transform"
          >
            Начать Путь 🚀
          </button>
        </motion.div>
      </div>
    );
  }

  const completed = new Set(progress.completed_days ?? []);
  const currentDay = progress.current_day;

  function nodeState(step: CourseStep): NodeState {
    if (completed.has(step.day)) return 'done';
    if (step.day === currentDay) return 'current';
    return 'future';
  }

  return (
    <div className="px-5 pt-6 pb-28 max-w-md mx-auto">
      <BackHeader onBack={() => nav(-1)} title="Путь со Славой" />

      <div className="mt-4 space-y-0.5">
        {steps.map((step, idx) => {
          const state = nodeState(step);
          const unlocked = isStepUnlocked(step, profile?.subscription_status);
          const isLast = idx === steps.length - 1;
          // Пройденные дни всегда можно перечитать; текущий — открыть, если сегодня ещё не делал
          const tappable = state === 'done' || (state === 'current' && unlocked && canStartToday(progress));

          return (
            <div key={step.id} className="flex gap-3">
              {/* Рельса с бусиной и линией */}
              <div className="flex flex-col items-center w-6 shrink-0">
                <div
                  className={[
                    'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border-2 shrink-0 transition-all',
                    state === 'done' && 'bg-emerald-glow border-emerald-glow text-bg',
                    state === 'current' && 'border-emerald-glow text-emerald-glow bg-emerald-glow/10 ring-4 ring-emerald-glow/15',
                    state === 'future' && 'border-line text-ink-dim bg-bg-elevated',
                  ].filter(Boolean).join(' ')}
                >
                  {state === 'done'
                    ? (MILESTONE[step.day] ?? <Check size={13} strokeWidth={3} />)
                    : (MILESTONE[step.day] ?? step.day)}
                </div>
                {!isLast && (
                  <div
                    className={
                      state === 'done'
                        ? 'w-0.5 flex-1 min-h-[1.6rem] bg-emerald-glow/50 my-1'
                        : 'w-0 flex-1 min-h-[1.6rem] border-l-2 border-dashed border-line my-1'
                    }
                  />
                )}
              </div>

              {/* Подпись шага */}
              <button
                type="button"
                disabled={!tappable}
                onClick={() => {
                  if (!tappable) return;
                  haptic('light');
                  nav(`/path/step/${step.day}`);
                }}
                className={[
                  'flex-1 text-left pb-4 min-w-0',
                  tappable ? 'active:opacity-70' : 'cursor-default',
                ].join(' ')}
              >
                <div className="text-[10px] uppercase tracking-wide flex items-center gap-1.5"
                  style={{ color: state === 'current' ? 'var(--tw-emerald,#10b981)' : undefined }}>
                  <span className={state === 'current' ? 'text-emerald-glow font-semibold' : 'text-ink-dim'}>
                    {state === 'current' ? 'Ты здесь · ' : ''}День {step.day}
                  </span>
                  {MILESTONE[step.day] && state !== 'future' && (
                    <span className="text-warn font-bold">
                      {step.day === 3 ? 'СТАРТ ПРОЙДЕН' : step.day === 21 ? 'ВЕХА' : ''}
                    </span>
                  )}
                </div>
                <div className={[
                  'text-sm font-medium leading-snug flex items-center gap-1.5',
                  state === 'future' ? 'text-ink-dim' : 'text-ink',
                ].join(' ')}>
                  {/* Впереди показываем интригующий заголовок (curiosity gap) */}
                  {state === 'future' ? (step.teaser_title || step.title) : step.title}
                  {state === 'future' && !unlocked && <Lock size={12} className="text-ink-dim shrink-0" />}
                </div>
                {tappable && (
                  <div className="text-[11px] text-emerald-glow mt-0.5">
                    {state === 'done' ? 'Перечитать →' : 'Открыть шаг →'}
                  </div>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {!progress.completed_at && (
        canStartToday(progress) ? (
          <button
            onClick={() => {
              haptic('medium');
              const cur = steps.find((s) => s.day === progress.current_day);
              const unlocked = cur ? isStepUnlocked(cur, profile?.subscription_status) : true;
              nav(unlocked ? `/path/step/${progress.current_day}` : '/paywall');
            }}
            className="w-full mt-5 py-3.5 rounded-xl bg-emerald-glow text-bg font-bold active:scale-[0.98] transition-transform"
          >
            Открыть шаг · День {progress.current_day} →
          </button>
        ) : (
          <div className="w-full mt-5 py-3.5 rounded-xl bg-bg-elevated border border-line text-ink-dim font-medium text-center text-sm">
            🌙 Шаг на сегодня сделан — возвращайся завтра за днём {progress.current_day}
          </div>
        )
      )}

      {progress.completed_at && (
        <button
          onClick={() => { haptic('light'); nav('/path/milestone'); }}
          className="w-full mt-4 py-3 rounded-xl bg-warn/15 border border-warn/40 text-warn font-semibold active:scale-[0.98]"
        >
          🎉 Открыть веху
        </button>
      )}
    </div>
  );
}

function BackHeader({ onBack, title }: { onBack: () => void; title?: string }) {
  return (
    <div className="flex items-center gap-2">
      <button onClick={onBack} className="w-9 h-9 -ml-2 rounded-full flex items-center justify-center text-ink-dim active:bg-bg-elevated">
        <ChevronLeft size={20} />
      </button>
      {title && <div className="font-display font-bold text-lg">{title}</div>}
    </div>
  );
}
